/**
 * @description renders the Select All checkbox that appears at the top of the list
 */

import React from 'react';
import { useFormContext } from 'react-hook-form';
import Checkbox from '@mui/material/Checkbox';
import { DateTime } from 'luxon';

import { STATUSES } from '../constants';

const SelectAllBox = () => {
  const { setValue, watch, getValues } = useFormContext();
  const personnel = watch('personnel');
  const selectedPersonnel = watch('selectedPersonnel');

  // Determine the checked and indeterminate statuses based on if the count of selected personnel and count of personnel in the form
  const [isChecked, isIndeterminate] = React.useMemo(() => {
    const selectedPersonnelCount = Object.keys(selectedPersonnel).length;

    // If the count of personnel and the count of selected personnel match, mark checked
    const checked = personnel.length === selectedPersonnelCount;

    // Mark indeterminate if not checked but there are some selected personnel
    const indeterminate = !checked && selectedPersonnelCount > 0;

    return [checked, indeterminate];
  }, [personnel, selectedPersonnel]);

  /**
   * Handles checkbox change events by marking all personnel as selected
   * @param {boolean} checked  - indicates if the checkbox is checked or not
   */
  function handleChange (checked) {
    let newSelectedPersonnel = {};
    let dateAssigned = null;
    let status = null;

    if (checked) {
      newSelectedPersonnel = personnel.reduce((obj, entry) => {
        // eslint-disable-next-line no-param-reassign
        obj[entry.personnelId] = true;

        return obj;
      }, {});
      dateAssigned = DateTime.now();
      status = STATUSES[1];
    }

    setValue('selectedPersonnel', newSelectedPersonnel, { shouldDirty: true, shouldValidate: true });

    // Iterate through the personnel list and set the date assigned
    for (let index = 0; index < personnel.length; index++) {
      const [currDateAssigned, currStatus] = getValues([`personnel.${ index }.dateAssigned`, `personnel.${ index }.status`]);

      setValue(`personnel.${ index }.status`, status === null ? null : (currStatus ?? status), { shouldDirty: true });
      setValue(`personnel.${ index }.dateAssigned`, dateAssigned === null ? null : (currDateAssigned ?? dateAssigned), { shouldDirty: true });
    }
  }

  return (
    <Checkbox
      onChange={(evt) => handleChange(evt.target.checked)}
      checked={isChecked}
      indeterminate={isIndeterminate}
    />
  );
};

export default SelectAllBox;
